import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {PathService} from './path.service';
import {Router} from '@angular/router';
import {from, Observable, of, throwError} from 'rxjs';
import {AnyMap, OkInfo, StringMap} from '../util/interfaces';
import {catchError, finalize, map} from 'rxjs/operators';
import {StateService} from './state.service';
import {SessionService} from './session.service';

export type UserType = 'super'|'admin'|'user'|'print';

export interface AuthInfo {
    token: string;
    idle: number;
    type: UserType;
}
export interface UserInfo {
    id: number;
    email: string;
    type: UserType;
    disabled_at: string;
    sessions?: number;
}


@Injectable({
    providedIn: 'root'
})
export class AuthService {

    private readonly authRoot: string;
    private readonly usersRoot: string;
    private readonly envRoot: string;
    private lastLogoutTime: number;

    constructor(private http: HttpClient,
                private state: StateService,
                private sessionService: SessionService,
                private pathService: PathService,
                private router: Router) {
        this.authRoot = `${this.pathService.apiRoot}/auth`;
        this.usersRoot = `${this.pathService.apiRoot}/users`;
        this.envRoot = `${this.pathService.apiRoot}/env`;
    }

    /** Receives user email and password, validates if they correspond to a validly registered user, and if so returns
     * an observable with the auth token assigned to the user for the session created for this, and type to indicate what type of user
     * this is. If invalid, an exception will be thrown.
     *
     * @param userEmail
     * @param password
     */
    logIn(userEmail: string, password: string): Observable<AuthInfo> {
        return this.http.post<AuthInfo>(`${this.authRoot}/login`, {email: userEmail, password: password});
    }

    /** Checks whether a given email address would be available for use for a new user
     *
     * @param userEmail the address to check
     * @return an Observable with true or false
     */
    isEmailAvailable(userEmail: string): Observable<boolean> {
        return this.http.post<OkInfo>(`${this.authRoot}/available`, {email: userEmail}).pipe(
            map((info) => info.ok)
        );
    }

    /** Tells server to extend the current user session's expiration, just as if there had been a meaningful interaction with the server.
     * Intended for background use in cases where user is expected to leave the session idle for a long time.
     */
    keepAlive(): Observable<OkInfo> {
        return this.http.put<OkInfo>(`${this.authRoot}/keep_alive`, {});
    }

    /** Closes the current user session, if any indicated in bearer token.
     * Does nothing if token's session is already closed.
     * Fails if token not present or invalid.
     * [Request must carry auth token header]
     */
    logOut(): Observable<OkInfo> {
        return this.http.put<OkInfo>(`${this.authRoot}/logout`, {});
    }

    /** Redirects user to login page, remembering what target URL the user really wanted to reach, so they get sent to it after login, if
     * successful
     *
     * @param targetUrl
     */
    redirectToLogIn(targetUrl?:string) {
        from(this.router.navigateByUrl('/login')).subscribe(() => {
            this.state.set('targetUrl', targetUrl);
        });
    }

    /** Convenience method that tries to log the user out, waits for the result and, whether it succeeds or not, it
     * then clears state related to user session and then redirects to home page.
     */
    logOutThenCleanUpAndRedirect() {
        const now = Date.now();
        if (this.lastLogoutTime && now - this.lastLogoutTime < 1000) {
            console.warn("Repeat logout too fast - ignored");
            return;
        }
        this.lastLogoutTime = now;

        this.logOut().pipe(
            catchError((err) => {
                // If we receive an error 401 status (unauthorized), just ignore it and replace with success response
                if (err instanceof HttpErrorResponse && err.status == 401)
                    return of({ok:true});
                // Otherwise, rethrow the error
                return throwError(err);
            }),
            finalize(() => {
                this.cleanupAfterLogout();
            })
        ).subscribe(() => {
        });
    }

    /** Standard sequence to execute after a successful logout: Cleans up state and navigates to home page.
     */
    cleanupAfterLogout() {
        this.sessionService.clearSession();
        this.state.clearAll();
        this.router.navigateByUrl('/login').then();
    }

    /** Updates the current user's password
     *
     * @param oldPassword previous password: must match what's hashed in db
     * @param newPassword new password
     */
    updatePassword(oldPassword: string, newPassword: string): Observable<OkInfo> {
        return this.http.put<OkInfo>(`${this.authRoot}/password`, {oldPassword: oldPassword, newPassword: newPassword});
    }

    /** Requests that an email be sent to the given address with a link to a page where the user can set a new password.
     * It fails if the email address does not correspond to a valid user.
     *
     * @param userEmail the user's email
     */
    requestPasswordReset(userEmail: string): Observable<OkInfo> {
        return this.http.post<OkInfo>(`${this.authRoot}/request_password_reset`, {email: userEmail});
    }

    /** Changes the password for the user linked to the given reset token to the new given password string
     *
     * @param userEmail the user's email
     * @param token
     * @param newPassword
     */
    resetPassword(userEmail: string, token: string, newPassword: string): Observable<any> {
        // return observableOf(null);
        return this.http.post<OkInfo>(`${this.authRoot}/reset_password`,
            {email: userEmail, token: token, password: newPassword});
    }

    /** Retrieves the current environment
     */
    getEnvironment(): Observable<string> {
        return this.http.get<StringMap>(this.envRoot).pipe(
            map((out) => out.env)
        );
    }

    /** Retrieves an array with info for all registered users.
     *
     * @return Observable<UserInfo[]>
     */
    getUsers(): Observable<UserInfo[]> {
        return this.http.get<AnyMap>(this.usersRoot).pipe(
            map((info: AnyMap) => info.users)
        );
    }

    /** Adds a new user with the given type. It will get a random password assigned initially
     * so it will be required to go through "forgot password" path.
     *
     * @param email
     * @param type the type of user being added
     * @param name (optional) the user's full name, if known
     * @return Observable<OkInfo>
     */
    addUser(email:string, type:UserType, name?:string): Observable<OkInfo> {
        let body = {email:email, type:type};
        if (name)
            body['name'] = name;
        return this.http.post<OkInfo>(this.usersRoot, body);
    }

    /** Updates a user's info. Only disabled and type are allowed to be changed.
     *
     * @param id
     * @param disabled
     * @param type
     * @return Observable<OkInfo>
     */
    updateUser(id:number, disabled: boolean, type:UserType): Observable<OkInfo> {
        return this.http.put<OkInfo>(this.usersRoot, {id:id, disabled:disabled, type: type});
    }

    /** Tries to delete the user with the given id. It will fail if the user has had any activity (user sessions).
     *
     * @param id
     * @return Observable<OkInfo>
     */
    deleteUser(id:number): Observable<OkInfo> {
        return this.http.delete<OkInfo>(`${this.usersRoot}/${id}`);
    }
}
